import { useMemo } from "react";
import MaharashtraDistrictMapSvg from "@/components/MaharashtraDistrictMapSvg";
import MapDistrictDetailCard, {
  buildDistrictOverviewCard,
  buildPatCard,
  buildPgiCard,
  buildSqaafCard,
} from "@/components/MapDistrictDetailCard";

export const MAP_METRIC_OPTIONS = [
  { key: "shi", label: "Overall School Health", legend: "Overall School Health (OSH)" },
  { key: "aadhaar_coverage", label: "Student Identity Index", legend: "Student Identity Index" },
  { key: "infrastructure_index", label: "Infrastructure Overview", legend: "Infrastructure Overview" },
  { key: "ctet_qualified_pct", label: "Teacher Quality Index", legend: "Teacher Quality Index" },
];

const FILL = {
  noData: "#e8f1fb",
  low: "#cfe2ff",
  mid: "#88b9ff",
  high: "#3b82f6",
  selected: "#2563eb",
};

const getTier = (value) => {
  if (value == null) return "no-data";
  if (value >= 70) return "high";
  if (value >= 50) return "mid";
  return "low";
};

const ExecutiveMapPanel = ({
  data,
  selectedDistrict,
  onSelectDistrict,
  selectedMetric = "shi",
  onMetricChange,
  sqaafOverview = null,
}) => {
  const districtDataMap = useMemo(() => {
    if (!data?.districts) return {};
    const map = {};
    data.districts.forEach((d) => {
      map[d.district_name] = d;
    });
    return map;
  }, [data]);

  const metricMeta = MAP_METRIC_OPTIONS.find((m) => m.key === selectedMetric) || MAP_METRIC_OPTIONS[0];

  const getDistrictFill = (districtName) => {
    if (districtName === selectedDistrict) return FILL.selected;
    const district = districtDataMap[districtName];
    if (!district?.has_data) return FILL.noData;
    const value = district.metrics?.[selectedMetric];
    const tier = getTier(value);
    if (tier === "no-data") return FILL.noData;
    return FILL[tier];
  };

  const selectedData = selectedDistrict ? districtDataMap[selectedDistrict] : null;

  const detailCards = useMemo(() => {
    if (!selectedData?.has_data) return [];
    return [
      buildDistrictOverviewCard(selectedDistrict, selectedData, metricMeta, selectedMetric),
      buildPgiCard(selectedData),
      buildSqaafCard(selectedDistrict, sqaafOverview),
      buildPatCard(selectedData),
    ].filter(Boolean);
  }, [selectedData, selectedDistrict, metricMeta, selectedMetric, sqaafOverview]);

  if (!data) {
    return (
      <article className="ed-map-panel">
        <div className="ed-map-stage ed-map-loading">Loading map data…</div>
      </article>
    );
  }

  return (
    <article className="ed-map-panel" data-testid="maharashtra-map">
      <div className="ed-map-panel-head">
        <div>
          <h2>Maharashtra District Map</h2>
          <p>Click a district to review its school health score and coverage indicators.</p>
        </div>
        <select
          className="ed-map-select"
          aria-label="Select map metric"
          value={selectedMetric}
          onChange={(e) => onMetricChange?.(e.target.value)}
          data-testid="metric-selector"
        >
          {MAP_METRIC_OPTIONS.map((opt) => (
            <option key={opt.key} value={opt.key}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="ed-map-body">
        <div className="ed-map-stage" aria-label="Maharashtra district map visualization">
          <MaharashtraDistrictMapSvg
            districtDataMap={districtDataMap}
            selectedDistrict={selectedDistrict}
            onSelectDistrict={onSelectDistrict}
            getDistrictFill={getDistrictFill}
          />

          <div className="ed-map-legend">
            <div className="ed-legend-title">{metricMeta.legend}</div>
            <div className="ed-legend-scale">
              <span>Low</span>
              <i className="ed-scale" />
              <span>High</span>
            </div>
          </div>

          <div className="ed-map-hint">
            <span className="ed-no-data-key" />
            <span>No Data</span>
            <span className="ed-click-hint">ⓘ Click district for details</span>
          </div>
        </div>

        <aside className="ed-map-detail-rail" aria-label="District framework details">
          {detailCards.length ? (
            detailCards.map((card) => (
              <MapDistrictDetailCard
                key={card.title}
                title={card.title}
                scoreLabel={card.scoreLabel}
                score={card.score}
                badge={card.badge}
                rows={card.rows}
                emptyMessage={card.emptyMessage}
              />
            ))
          ) : (
            <article className="ed-map-detail-card ed-map-detail-card-placeholder">
              <b>Select a district</b>
              <p>Click a district on the map to view OSH, PGI, SQAAF, and PAT details.</p>
            </article>
          )}
        </aside>
      </div>
    </article>
  );
};

export default ExecutiveMapPanel;
